<script setup lang="ts">
import { computed } from 'vue'

import { NOTE_TITLE_MAX_LENGTH } from '@/scripts/app-configs/constants'
import useSwordForm from '@/composables/useSwordForm'
import { baseNoteSchema } from '@/utils/zod/schemas/patient/member-notes'

const props = withDefaults(defineProps<{
  titleValue?: string
  bodyValue?: string
}>(), {
  titleValue: '',
  bodyValue: '',
})

const emit = defineEmits<{
  (e: 'title', value: string): void
  (e: 'body', value: string): void
  (e: 'submit-form'): void
  (e: 'cancel'): void
  (e: 'new-note'): void
}>()

const title = computed({
  get: () => props.titleValue,
  set: (value) => emit('title', value),
})

const body = computed({
  get: () => {
    const parser = new DOMParser()
    const dom = parser.parseFromString(props.bodyValue, 'text/html')

    return dom.body.textContent
  },
  set: (value) => emit('body', value),
})

const formState = computed(() => ({
  title: title.value,
  body: body.value,
}))

const { $v } = useSwordForm({ validationSchema: baseNoteSchema, formState })

const submitForm = () => emit('submit-form')
</script>

<template>
  <form
    class="note-form-wrapper"
    name="noteEdition"
    @submit.prevent="submitForm()"
  >
    <section class="note-fields -pxy_0">
      <template>
        <base-input-wuk
          v-model="title"
          class="-pxy_2"
          name="noteTitle"
          :placeholder="$t('NOTES.TITLE')"
          :maxlength="NOTE_TITLE_MAX_LENGTH"
          :validation="$v.title"
        />
        <textarea
          v-model="body"
          class="note-body sword-base-input-wuk -pt_0 -px_2 -pb_1"
          name="noteBody"
          :placeholder="$tc('copy_2829')"
          autocomplete="off"
          @focus="$v.body.$reset()"
          @blur="$v.body.$touch()"
        ></textarea>
        <input-error-wuk
          v-if="$v.body.$invalid"
          class="note-error-feedback -pxy_2"
          :validator="$v.body"
        />
      </template>
    </section>
    <!-- BOTTOM -->
    <section class="notes-footer">
      <sword-button-wuk
        text="COMMON.CANCEL"
        icon="close"
        side="left"
        class="cancel-button"
        @click.native="$emit('cancel')"
      />
      <sword-button-wuk
        text="copy_2684"
        icon="add"
        side="left"
        type="submit"
        :disabled="$v.$invalid"
        @click.native="$emit('new-note')"
      />
    </section>
  </form>
</template>

<style lang="scss" scoped>
.note-body {
  outline: none;
  resize: none;
  height: 100%;
  @include custom-scrollbar-wuk(getColor($blues-wuk, 5), 5px, getColor($blues-wuk, 2));
}

.note-form-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.note-fields {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.note-error-feedback {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.notes-footer {
  display: grid;
  grid: min-content / 1fr 1fr;

  .sword-button-wuk {
    height: 4rem;
    font-size: 1.375rem;
    margin: 0;
    border-radius: 0;

    &.cancel-button {
      background: $context-color-7;
      color: $primary-color-dark;

      &:hover {
        background: $context-color-6;
      }
    }

    ::v-deep {
      .sword-icon.svg-icon {
        height: 1.25rem;
        width: 1.25rem;
      }

      span {
        font-weight: 500;
      }
    }
  }
}

</style>
